// VIDEO-EDITING — Shared pure timeline math for the clip-based editor.
//
// `clips[]` (see types.ts) is the project timeline. This module holds the
// pure functions that operate on it — no zod, no React, no Remotion
// imports — so the BE renderer, the FE editor (via the
// `@wiser/remotion-layers` alias), and the agent tools all share one
// implementation and never drift. This is the single source of truth the
// b-roll ripple-insert plan refers to as `remotion/layers/timeline.ts`.
//
// Ripple insert/delete are SYMMETRIC: `insertBrollAt` splits a source clip
// and pushes everything after it later; `removeClipRipple` deletes a clip,
// pulls everything after it back, and merges the now-adjacent source clips
// so an insert followed by a delete returns the exact original timeline.
import type {
  BrollClip,
  CaptionSegment,
  Clip,
  ClipTransitionKind,
  Overlay,
  VideoEditSpec,
} from "./types";

// Tolerance for floating-point boundary comparisons. Insert points snap to
// caption-segment / clip boundaries, but those arrive as floats from probed
// durations — `EPS` keeps a value a hair off a boundary from minting a
// zero-length clip sliver.
const EPS = 1e-6;
const TRANSITION_FPS = 30;

function withoutTransition(clip: Clip): Clip {
  const copy = { ...clip };
  delete copy.transitionInSec;
  delete copy.transitionInKind;
  return copy;
}

export function maxTransitionInSecForDurations(
  prevDurationSec: number,
  clipDurationSec: number,
  fps = TRANSITION_FPS,
): number {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : TRANSITION_FPS;
  const prevFrames = Math.max(1, Math.round(Math.max(0, prevDurationSec) * safeFps));
  const clipFrames = Math.max(1, Math.round(Math.max(0, clipDurationSec) * safeFps));
  const maxFrames = Math.min(prevFrames, clipFrames) - 1;
  return maxFrames > 0 ? maxFrames / safeFps : 0;
}

export function clampTransitionInSec(
  prev: Clip,
  clip: Clip,
  transitionInSec: number,
  fps = TRANSITION_FPS,
): number {
  if (!Number.isFinite(transitionInSec) || transitionInSec <= EPS) return 0;
  return Math.max(
    0,
    Math.min(
      transitionInSec,
      maxTransitionInSecForDurations(
        durationOfClip(prev),
        durationOfClip(clip),
        fps,
      ),
    ),
  );
}

export function effectiveTransitionInSec(
  clips: readonly Clip[],
  index: number,
  fps = TRANSITION_FPS,
): number {
  if (index <= 0) return 0;
  const clip = clips[index];
  const prev = clips[index - 1];
  if (!clip || !prev) return 0;
  return clampTransitionInSec(prev, clip, clip.transitionInSec ?? 0, fps);
}

function normalizeClipTransitions(clips: readonly Clip[]): Clip[] {
  return clips.map((clip, index) => {
    if (index <= 0) return withoutTransition(clip);
    const transitionInSec = clampTransitionInSec(
      clips[index - 1],
      clip,
      clip.transitionInSec ?? 0,
    );
    if (transitionInSec <= EPS) return withoutTransition(clip);
    return {
      ...clip,
      transitionInSec,
      ...(clip.transitionInKind ? { transitionInKind: clip.transitionInKind } : {}),
    };
  });
}

/**
 * Playback length of a single clip — its trimmed sub-range. Clamped at 0 so
 * a malformed clip (trimEndSec <= trimStartSec) never contributes negative
 * time; the zod `ClipSchema` already rejects that shape at the trust
 * boundary, this is just defence in depth for the renderer.
 */
export function durationOfClip(clip: Clip): number {
  return Math.max(0, clip.trimEndSec - clip.trimStartSec);
}

/**
 * Per-boundary crossfade overlap — clip[i].transitionInSec when `i > 0`,
 * else 0 (no preceding clip). Negative values are clamped to 0 so a
 * malformed write can't lengthen the timeline.
 */
function transitionAt(clips: readonly Clip[], i: number): number {
  return effectiveTransitionInSec(clips, i);
}

/**
 * Total timeline length — Σ clip durations minus Σ transition overlaps
 * (each crossfade is shared between two adjacent clips, so it eats from
 * the combined length once). Drives `dimensions.durationSec` whenever
 * `clips` is non-empty; never user-stored.
 */
export function durationOfClips(clips: readonly Clip[]): number {
  let total = 0;
  for (let i = 0; i < clips.length; i++) {
    total += durationOfClip(clips[i]) - transitionAt(clips, i);
  }
  return total;
}

/**
 * Shift one timed range by `deltaSec` — moving `start`, `end`, AND any
 * nested per-word timings in lockstep.
 *
 * A caption segment carries an optional `words[]`, each word with its own
 * absolute `[start, end]`, that drives the `word-by-word-accent` preset. A
 * ripple insert/delete must move those words by the same delta as the
 * segment or the per-word highlight drifts out of sync after the splice.
 * Plain `{start, end}` ranges have no `words` — the `Array.isArray` guard
 * makes the deep-shift a no-op for them.
 */
function shiftRange<T extends { start: number; end: number }>(
  range: T,
  deltaSec: number,
): T {
  const shifted: T = {
    ...range,
    start: range.start + deltaSec,
    end: range.end + deltaSec,
  };
  // `T` is constrained to `{start, end}` only, so `words` is reached via a
  // cast. When present it is an array of `{start, end, word}`; each word's
  // own [start, end] shifts by the same delta as the parent segment.
  const words = (range as { words?: unknown }).words;
  if (Array.isArray(words)) {
    (shifted as { words?: unknown }).words = words.map((w) => ({
      ...(w as Record<string, unknown>),
      start: (w as { start: number }).start + deltaSec,
      end: (w as { end: number }).end + deltaSec,
    }));
  }
  return shifted;
}

/**
 * Ripple-shift caption-style timed ranges. Every range whose `start` is at
 * or after `atSec` moves by `deltaSec` (a whole-range shift via
 * `shiftRange` — `start`, `end`, and any per-word timings move together,
 * never a split); ranges before `atSec` are returned untouched.
 *
 * Inserts land on segment boundaries (plan decision 4), so in practice no
 * caption segment straddles `atSec`. A straddling range here (start before
 * `atSec`) is left in place rather than stretched — the caller is
 * responsible for boundary snapping.
 */
export function shiftTimedRanges<T extends { start: number; end: number }>(
  ranges: readonly T[],
  atSec: number,
  deltaSec: number,
): T[] {
  return ranges.map((r) =>
    r.start >= atSec - EPS ? shiftRange(r, deltaSec) : { ...r },
  );
}

/**
 * Segment-level ripple for a clip *removal* — the delete-direction mirror of
 * `shiftTimedRanges`. Ranges whose `start` falls inside the removed window
 * `[r0, r1)` are dropped (they belonged to the deleted clip and have nowhere
 * to ripple to); ranges at or after `r1` shift back by the window length;
 * ranges before `r0` are returned untouched.
 *
 * `removeClipRipple` calls this for the active language's caption segments;
 * the FE persist path calls it directly to apply the identical whole-segment
 * shift to every *other* language so the parallel `translations[].segments[]`
 * arrays stay aligned with the active one. Surviving ranges shift via
 * `shiftRange`, so per-word timings ripple with their segment.
 */
export function dropAndShiftRanges<T extends { start: number; end: number }>(
  ranges: readonly T[],
  r0: number,
  r1: number,
): T[] {
  const delta = r1 - r0;
  return ranges
    .filter((r) => !(r.start >= r0 - EPS && r.start < r1 - EPS))
    .map((r) => (r.start >= r1 - EPS ? shiftRange(r, -delta) : { ...r }));
}

export interface SplitResult {
  /** The clips array — the clip containing `atSec` split in two if needed. */
  clips: Clip[];
  /** Index in `clips` at which inserted content should land. */
  index: number;
  /** True when a clip was actually split (false when `atSec` was a boundary). */
  didSplit: boolean;
}

/**
 * Split the timeline at `atSec` (a time in the *final* video). The clip
 * spanning `atSec` becomes two `source` clips referencing the same asset
 * with adjacent trim ranges — no media file is split. When `atSec` already
 * falls on a clip boundary nothing is split.
 *
 * `newId` mints the id for the second half; it must return an id unique
 * within the project (the caller owns id generation — cuid on the BE, a
 * uuid slice on the FE — so this stays pure and deterministic per input).
 */
export function splitClipAt(
  clips: readonly Clip[],
  atSec: number,
  newId: () => string,
): SplitResult {
  if (atSec <= EPS) {
    return { clips: clips.map((c) => ({ ...c })), index: 0, didSplit: false };
  }
  let offset = 0;
  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    const dur = durationOfClip(clip);
    const localT = atSec - offset;
    if (localT <= EPS) {
      // `atSec` sits on clip i's start boundary — insert before it, no split.
      return { clips: clips.map((c) => ({ ...c })), index: i, didSplit: false };
    }
    if (localT < dur - EPS) {
      // `atSec` is strictly inside clip i — split it.
      const cut = clip.trimStartSec + localT;
      const head: Clip = { ...clip, trimEndSec: cut };
      // The original clip's `transitionInSec` belongs to its leading edge
      // — that boundary is now HEAD's, not TAIL's. Clearing it on the tail
      // avoids the inverse-split (`mergeAdjacentSourceClips` after a delete)
      // doubling up the crossfade.
      const tail: Clip = {
        ...clip,
        id: newId(),
        trimStartSec: cut,
        transitionInSec: undefined,
        transitionInKind: undefined,
      };
      const out = [
        ...clips.slice(0, i).map((c) => ({ ...c })),
        head,
        tail,
        ...clips.slice(i + 1).map((c) => ({ ...c })),
      ];
      return { clips: out, index: i + 1, didSplit: true };
    }
    offset += dur;
  }
  // `atSec` is at or beyond the end of the timeline — append.
  return { clips: clips.map((c) => ({ ...c })), index: clips.length, didSplit: false };
}

/**
 * Collapse runs of adjacent `source` clips that reference the same asset
 * with touching trim ranges back into one clip. Applied after a delete so
 * removing a b-roll clip re-fuses the source halves it had split — the
 * inverse of `splitClipAt`, which is what makes insert/delete symmetric.
 */
function mergeAdjacentSourceClips(clips: readonly Clip[]): Clip[] {
  const out: Clip[] = [];
  for (const clip of clips) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.kind === "source" &&
      clip.kind === "source" &&
      prev.url === clip.url &&
      Math.abs(prev.trimEndSec - clip.trimStartSec) <= EPS
    ) {
      out[out.length - 1] = { ...prev, trimEndSec: clip.trimEndSec };
    } else {
      out.push({ ...clip });
    }
  }
  return out;
}

/**
 * Ripple overlays by `deltaSec` at `atSec` — shared by `insertBrollAt` (a
 * b-roll splice, `deltaSec > 0`) and `retrimClip` (a b-roll trim, where
 * shrinking the clip gives `deltaSec < 0`). Endpoints remap independently
 * so a straddling overlay is *extended/shrunk* (its `endSec` moves, its
 * `startSec` does not) — it persists across the b-roll rather than being
 * split, which would orphan its `overlayTranslations` entry (plan decision
 * 4). Overlays fully after `atSec` shift wholesale; overlays fully before
 * are untouched.
 */
function rippleOverlaysShift(
  overlays: readonly Overlay[],
  atSec: number,
  deltaSec: number,
): Overlay[] {
  return overlays.map((o) => ({
    ...o,
    startSec: o.startSec >= atSec - EPS ? o.startSec + deltaSec : o.startSec,
    endSec: o.endSec > atSec + EPS ? o.endSec + deltaSec : o.endSec,
  }));
}

/**
 * Ripple overlays for the removal of the window `[r0, r1]` (length
 * `deltaSec`). Each endpoint remaps through `removeRemap`: before the
 * window it is fixed, after it shifts back by `deltaSec`, inside it
 * collapses onto the window start. An overlay left with no positive
 * duration (it lived entirely inside the removed window) is dropped.
 */
function rippleOverlaysForRemove(
  overlays: readonly Overlay[],
  r0: number,
  r1: number,
  deltaSec: number,
): Overlay[] {
  const remap = (t: number): number => {
    if (t <= r0 + EPS) return t;
    if (t >= r1 - EPS) return t - deltaSec;
    return r0;
  };
  return overlays
    .map((o) => ({ ...o, startSec: remap(o.startSec), endSec: remap(o.endSec) }))
    .filter((o) => o.endSec - o.startSec > EPS);
}

/** Recompute `dimensions.durationSec` from the (post-ripple) clip list. */
function withDerivedDuration(
  spec: VideoEditSpec,
  clips: Clip[],
): VideoEditSpec["dimensions"] {
  return spec.dimensions
    ? { ...spec.dimensions, durationSec: durationOfClips(clips) }
    : spec.dimensions;
}

/**
 * Ripple-insert a b-roll clip at `atSec` (a time in the final video).
 *
 * The source clip spanning `atSec` is split, `broll` is spliced between the
 * halves, and everything after `atSec` is pushed `durationOfClip(broll)`
 * later: caption segments shift as whole segments, overlays shift or extend
 * on straddle, and `dimensions.durationSec` is re-derived. `broll` should
 * already be a `kind:'broll'`, `muted:true` clip with its trim range set.
 *
 * Only the active language's `captions.segments` are rippled here — the
 * other-language transcripts live on the Django project and are shifted by
 * the same whole-segment delta at persist time.
 */
export function insertClipAt(
  spec: VideoEditSpec,
  atSec: number,
  insertedClip: Clip,
  newId: () => string,
): VideoEditSpec {
  const delta = durationOfClip(insertedClip);
  const split = splitClipAt(spec.clips, atSec, newId);
  const clips = normalizeClipTransitions([
    ...split.clips.slice(0, split.index),
    { ...insertedClip },
    ...split.clips.slice(split.index),
  ]);
  return {
    ...spec,
    clips,
    captions: {
      ...spec.captions,
      segments: shiftTimedRanges(spec.captions.segments, atSec, delta),
    },
    overlays: rippleOverlaysShift(spec.overlays, atSec, delta),
    dimensions: withDerivedDuration(spec, clips),
  };
}

export function insertBrollAt(
  spec: VideoEditSpec,
  atSec: number,
  broll: Clip,
  newId: () => string,
  /**
   * Audio cover window in seconds — how much of the source narration the
   * broll covers visually + audibly. When omitted, defaults to the broll's
   * own clip duration (the legacy behavior). The broll's *trim* may extend
   * beyond this window to accommodate fade transitions: those extra
   * seconds are eaten by the @remotion/transitions crossfade and never
   * appear as solid b-roll, so the source 2 trim-start advance + the
   * captions ripple must use the cover window, NOT the clip duration.
   */
  coverDurationSec?: number,
): VideoEditSpec {
  const inserted = insertClipAt(spec, atSec, broll, newId);
  // Cover-mode contract: when the broll keeps the underlying narration
  // playing (`sourceAudioMode: 'continue'`), it should also REPLACE the
  // visual + audio window of the source split that follows. Without
  // advancing that split's `trimStartSec` by the cover window the
  // user hears the same narration twice — once as the underlay during
  // the broll, then again on the next source clip. Total timeline
  // length is preserved (cover, not extend).
  if (broll.sourceAudioMode !== 'continue') return inserted;
  const coverDuration =
    coverDurationSec != null && coverDurationSec > 0
      ? coverDurationSec
      : durationOfClip(broll);
  if (!(coverDuration > 0)) return inserted;
  const idx = inserted.clips.findIndex((c) => c.id === broll.id);
  const next = idx >= 0 ? inserted.clips[idx + 1] : undefined;
  if (!next || next.kind !== 'source') return inserted;
  const proposedStart = next.trimStartSec + coverDuration;
  // Don't drop the next clip past its own trimEnd — leave at least an
  // EPS-sized window so the spec stays valid and the renderer doesn't
  // produce a zero-frame Sequence.
  const safeStart = Math.min(proposedStart, Math.max(0, next.trimEndSec - EPS));
  if (Math.abs(safeStart - next.trimStartSec) <= EPS) return inserted;
  const adjustedClips = normalizeClipTransitions(
    inserted.clips.map((c, i) =>
      i === idx + 1 ? { ...c, trimStartSec: safeStart } : { ...c },
    ),
  );
  // Also ripple the captions + overlays back by `coverDuration` from
  // the point where the next source clip's audio now resumes, so the
  // transcript timing tracks the new (shorter) source range. Without
  // this the caption stays under the broll cutaway and the narration
  // continues without its caption.
  const captionsShiftAt = atSec + coverDuration;
  return {
    ...inserted,
    clips: adjustedClips,
    captions: {
      ...inserted.captions,
      segments: shiftTimedRanges(
        inserted.captions.segments,
        captionsShiftAt,
        -coverDuration,
      ),
    },
    overlays: rippleOverlaysShift(inserted.overlays, captionsShiftAt, -coverDuration),
    dimensions: withDerivedDuration({ ...inserted, clips: adjustedClips }, adjustedClips),
  };
}

/**
 * Migrate a LEGACY cover-mode broll timeline (brolls spliced into `clips[]`
 * with `sourceAudioMode:'continue'`) into the overlay model (`brolls[]`
 * rendered on top of a single continuous source clip).
 *
 * Why: the splice model only stays in lip-sync if total length is exactly
 * preserved (broll trim extended by 2·fade, both fades full, cover fully
 * skipped). Real brolls break that invariant and the tail desyncs. The
 * overlay model never cuts the source, so it can't drift — but already-saved
 * projects still carry the spliced shape. This lifts them on read.
 *
 * Pure + idempotent: a spec with no cover-mode broll clips (already
 * migrated, or never spliced) is returned untouched. Only the clean
 * cover-mode case is migrated — a timeline mixing stills, hard-cut brolls,
 * or multiple source assets keeps the legacy clip path (the renderer still
 * draws `kind:'broll'` clips inline) so no manual edit is silently dropped.
 *
 * Recovery is exact for the common case: in the cover model timeline-time ==
 * source-video-time (length preserved), so a source split encodes the
 * covered window as `[prevSource.trimEnd, nextSource.trimStart]`.
 */
export function migrateSplicedBrollsToOverlays(spec: VideoEditSpec): VideoEditSpec {
  const hasCoverBrollClips = spec.clips.some(
    (c) => c.kind === "broll" && c.sourceAudioMode === "continue",
  );
  if (!hasCoverBrollClips) return spec;

  const allCoverShape = spec.clips.every(
    (c) =>
      c.kind === "source" ||
      (c.kind === "broll" && c.sourceAudioMode === "continue"),
  );
  const sourceClips = spec.clips.filter((c) => c.kind === "source");
  if (!allCoverShape || sourceClips.length === 0 || !spec.source) return spec;

  const findSourceBefore = (i: number): Clip | undefined => {
    for (let j = i - 1; j >= 0; j--) {
      if (spec.clips[j].kind === "source") return spec.clips[j];
    }
    return undefined;
  };
  const findSourceAfter = (i: number): Clip | undefined => {
    for (let j = i + 1; j < spec.clips.length; j++) {
      if (spec.clips[j].kind === "source") return spec.clips[j];
    }
    return undefined;
  };

  const lifted: BrollClip[] = [];
  for (let i = 0; i < spec.clips.length; i++) {
    const clip = spec.clips[i];
    if (!(clip.kind === "broll" && clip.sourceAudioMode === "continue")) continue;
    const prev = findSourceBefore(i);
    const next = findSourceAfter(i);
    const fade = clip.transitionInSec ?? 0;
    const solid = Math.max(0, durationOfClip(clip) - 2 * fade);
    const startSec = prev ? prev.trimEndSec : 0;
    const endSec = next
      ? next.trimStartSec
      : startSec + (solid > 0 ? solid : durationOfClip(clip));
    if (!(endSec > startSec + EPS)) continue;
    const windowDur = endSec - startSec;
    const trimStartSec = clip.trimStartSec;
    const assetMax =
      clip.assetDurationSec && clip.assetDurationSec > 0
        ? clip.assetDurationSec
        : clip.trimEndSec;
    const trimEndSec = Math.min(
      Math.max(assetMax, trimStartSec + EPS),
      trimStartSec + windowDur,
    );
    lifted.push({
      id: clip.id,
      url: clip.url,
      storageKey: clip.storageKey,
      mimeType: clip.mimeType,
      startSec,
      endSec,
      trimStartSec,
      trimEndSec: trimEndSec > trimStartSec ? trimEndSec : trimStartSec + windowDur,
      ...(clip.assetDurationSec != null
        ? { assetDurationSec: clip.assetDurationSec }
        : {}),
      muted: true,
      ...(fade > EPS
        ? {
            transitionSec: fade,
            transitionKind: clip.transitionInKind ?? "fade",
          }
        : {}),
    });
  }

  // Collapse the split source segments back to one continuous source clip.
  // The last source split ends at the source asset's out-point, so the full
  // duration is the furthest `trimEndSec` across the source clips.
  const sourceDurationSec = Math.max(...sourceClips.map((c) => c.trimEndSec));
  const base = sourceClips[0];
  const continuousSource: Clip = {
    id: base.id,
    kind: "source",
    url: base.url,
    storageKey: base.storageKey,
    mimeType: base.mimeType,
    trimStartSec: 0,
    trimEndSec: sourceDurationSec,
    muted: false,
  };

  return {
    ...spec,
    clips: [continuousSource],
    brolls: [...spec.brolls, ...lifted],
    dimensions: spec.dimensions
      ? { ...spec.dimensions, durationSec: sourceDurationSec }
      : spec.dimensions,
  };
}

/**
 * Ripple-retrim the clip with id `clipId` to a new `[trimStartSec,
 * trimEndSec]` sub-range — the timeline counterpart of the b-roll trim
 * handles.
 *
 * The clip's timeline START is fixed (the preceding clips are unchanged),
 * so only its own playback length changes; everything after the clip's
 * OLD end ripples by `delta = newDuration − oldDuration`. Caption segments
 * and overlays after the clip shift by `delta` — a pure shift, never a
 * drop: a muted b-roll window carries no caption words, so trimming it
 * neither strands nor splits a segment. `dimensions.durationSec` is
 * re-derived.
 *
 * Intended for `kind:'broll'` (muted) clips — retrimming a `source` clip
 * would desync its own captions and is not what the editor calls this for.
 * An unknown `clipId`, a degenerate range (`trimEndSec <= trimStartSec`),
 * or a no-op retrim returns `spec` unchanged (same reference).
 *
 * Only the active language's `captions.segments` ripple here; the
 * other-language transcripts are shifted by the same whole-segment delta
 * at persist time (see the FE `timelineOps` replay).
 */
export function retrimClip(
  spec: VideoEditSpec,
  clipId: string,
  trimStartSec: number,
  trimEndSec: number,
): VideoEditSpec {
  const idx = spec.clips.findIndex((c) => c.id === clipId);
  if (idx < 0) return spec;
  if (trimStartSec < -EPS || !(trimEndSec > trimStartSec + EPS)) return spec;

  const old = spec.clips[idx];
  let offset = 0;
  for (let i = 0; i < idx; i++) offset += durationOfClip(spec.clips[i]);
  const oldEnd = offset + durationOfClip(old);
  const delta = trimEndSec - trimStartSec - durationOfClip(old);
  // No measurable change to either endpoint — return the same reference so
  // a React caller skips a needless re-render.
  if (
    Math.abs(delta) <= EPS &&
    Math.abs(trimStartSec - old.trimStartSec) <= EPS
  ) {
    return spec;
  }

  const clips = normalizeClipTransitions(spec.clips.map((c, i) =>
    i === idx ? { ...c, trimStartSec, trimEndSec } : { ...c },
  ));

  return {
    ...spec,
    clips,
    captions: {
      ...spec.captions,
      segments: shiftTimedRanges(spec.captions.segments, oldEnd, delta),
    },
    overlays: rippleOverlaysShift(spec.overlays, oldEnd, delta),
    dimensions: withDerivedDuration(spec, clips),
  };
}

/**
 * Set (or clear) the crossfade duration at clip `clipId`'s leading edge —
 * the seam with the previous clip. Growing the transition shrinks the
 * rendered timeline (the crossfade overlap eats from both adjacent clips);
 * shrinking or clearing it lengthens. Captions/overlays at or after the
 * OLD seam shift by `-(newT − oldT)` so they stay aligned with the same
 * visual moment in the source media.
 *
 * Clamped at write time to leave at least one 30fps frame visible in both
 * adjacent sequences. `<TransitionSeries.Transition>` fails when the
 * transition consumes an entire neighboring sequence. A value of 0 (or
 * below the EPS noise floor) is stored as `undefined` so the persisted spec
 * stays minimal.
 *
 * No-op (returns the same reference) when: `clipId` is unknown, the clip
 * is at index 0 (no preceding clip), or the clamped duration and
 * presentation kind equal the old values.
 * Intended to be applied AFTER structural edits — `splitClipAt` /
 * `insertBrollAt` / `retrimClip` / `removeClipRipple` accumulate offsets
 * against raw clip durations, so mixing transitions with structural edits
 * can leave caption ripples off by up to the transition duration.
 */
export function setClipTransition(
  spec: VideoEditSpec,
  clipId: string,
  newTransitionInSec: number,
  transitionKind?: ClipTransitionKind,
): VideoEditSpec {
  const idx = spec.clips.findIndex((c) => c.id === clipId);
  if (idx <= 0) return spec;

  const target = spec.clips[idx];
  const prev = spec.clips[idx - 1];
  const rawOldT = target.transitionInSec ?? 0;
  const oldT = transitionAt(spec.clips, idx);
  const oldKind = oldT > EPS ? target.transitionInKind ?? "fade" : undefined;
  const clamped = clampTransitionInSec(prev, target, newTransitionInSec);
  const nextKind = clamped > EPS ? transitionKind ?? oldKind ?? "fade" : undefined;
  if (
    Math.abs(clamped - oldT) <= EPS &&
    Math.abs(rawOldT - oldT) <= EPS &&
    nextKind === oldKind
  ) {
    return spec;
  }

  // OLD rendered start of clip[idx] = Σdur(0..idx-1) − Σtransition(1..idx).
  // This is where clip[idx]'s first frame appears in the rendered timeline
  // BEFORE the transition change — and the anchor for the caption/overlay
  // ripple. (Captions inside the overlap window were already at the OLD
  // seam; shifting from `oldStart` keeps them aligned with the source media
  // when the seam moves.)
  let oldStart = 0;
  for (let i = 0; i < idx; i++) oldStart += durationOfClip(spec.clips[i]);
  for (let i = 1; i <= idx; i++) oldStart -= transitionAt(spec.clips, i);

  // Growing the transition shrinks the timeline by (clamped - oldT).
  const delta = -(clamped - oldT);

  const clips = normalizeClipTransitions(spec.clips.map((c, i) =>
    i === idx
      ? clamped > EPS
        ? { ...c, transitionInSec: clamped, transitionInKind: nextKind }
        : withoutTransition(c)
      : { ...c },
  ));

  return {
    ...spec,
    clips,
    captions: {
      ...spec.captions,
      segments: shiftTimedRanges(spec.captions.segments, oldStart, delta),
    },
    overlays: rippleOverlaysShift(spec.overlays, oldStart, delta),
    dimensions: withDerivedDuration(spec, clips),
  };
}

/**
 * Ripple-delete the clip with id `clipId`: remove it, pull everything after
 * it back by its duration, and merge the source clips it had separated.
 * The exact inverse of `insertBrollAt` — insert then remove restores the
 * original timeline. Caption segments that started inside the removed
 * window are dropped (a muted b-roll window legitimately carries no words);
 * overlays remap through the removed interval. An unknown `clipId` is a
 * no-op.
 */
export function removeClipRipple(
  spec: VideoEditSpec,
  clipId: string,
): VideoEditSpec {
  const idx = spec.clips.findIndex((c) => c.id === clipId);
  if (idx < 0) return spec;

  let offset = 0;
  for (let i = 0; i < idx; i++) offset += durationOfClip(spec.clips[i]);
  const delta = durationOfClip(spec.clips[idx]);
  const r0 = offset;
  const r1 = offset + delta;

  const clips = normalizeClipTransitions(mergeAdjacentSourceClips([
    ...spec.clips.slice(0, idx),
    ...spec.clips.slice(idx + 1),
  ]));

  // Drop segments that began inside the removed window (they belonged to the
  // deleted clip) and ripple later ones back — see `dropAndShiftRanges`.
  const segments = dropAndShiftRanges(
    spec.captions.segments,
    r0,
    r1,
  ) as CaptionSegment[];

  return {
    ...spec,
    clips,
    captions: { ...spec.captions, segments },
    overlays: rippleOverlaysForRemove(spec.overlays, r0, r1, delta),
    dimensions: withDerivedDuration(spec, clips),
  };
}
